import { useEffect, useState } from "react";
import { KeyRound, QrCode } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const STATUS_STYLE = {
  lunas: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  gagal: "bg-rose-500/10 text-rose-400 border-rose-500/20",
};

export default function PaymentHub() {
  const [config, setConfig] = useState(null);
  const [payments, setPayments] = useState([]);
  const [form, setForm] = useState({ server_key: "", client_key: "", mode: "sandbox" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/payments").then((r) => setPayments(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/payments/config", form);
      toast.success("Konfigurasi Midtrans tersimpan");
      setForm({ server_key: "", client_key: "", mode: "sandbox" });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="payment-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Pembayaran Online</h1>
        <p className="text-sm text-muted-foreground mt-1">Midtrans Snap — QRIS, GoPay, Virtual Account</p>
      </div>

      <Card className={`border ${config?.configured ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20"}`} data-testid="midtrans-status-card">
        <CardContent className="p-4 flex items-center gap-3">
          <QrCode className={`w-5 h-5 ${config?.configured ? "text-emerald-400" : "text-amber-400"}`} />
          {config?.configured ? (
            <p className="text-sm">Midtrans aktif ({config.mode}) — Client Key <span className="font-mono">{config.client_key}</span>, Server Key <span className="font-mono">{config.server_key_masked}</span></p>
          ) : (
            <p className="text-sm">Midtrans belum dikonfigurasi. Daftar di <span className="font-mono">dashboard.sandbox.midtrans.com</span>, lalu isi Server Key & Client Key di bawah. Pembayaran online akan aktif otomatis di semua form penjualan.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-card/60 border-border">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><KeyRound className="w-4 h-4 text-sky-400" /> Konfigurasi Midtrans</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5"><Label>Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="select-midtrans-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox (testing)</SelectItem>
                    <SelectItem value="production">Production (live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Server Key</Label><Input value={form.server_key} onChange={(e) => setForm({ ...form, server_key: e.target.value })} required placeholder="SB-Mid-server-xxx" data-testid="input-midtrans-server-key" /></div>
              <div className="space-y-1.5"><Label>Client Key</Label><Input value={form.client_key} onChange={(e) => setForm({ ...form, client_key: e.target.value })} required placeholder="SB-Mid-client-xxx" data-testid="input-midtrans-client-key" /></div>
              <Button type="submit" className="w-full" disabled={saving} data-testid="btn-save-midtrans">{saving ? "Menyimpan..." : "Simpan Konfigurasi"}</Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-card border-border shadow-sm overflow-x-auto">
          <CardHeader><CardTitle className="text-base font-heading">Riwayat Transaksi</CardTitle></CardHeader>
          <Table className="min-w-[560px]" data-testid="payments-table">
            <TableHeader>
              <TableRow><TableHead>Order ID</TableHead><TableHead>Nominal</TableHead><TableHead>Status</TableHead><TableHead>Metode</TableHead><TableHead>Waktu</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {!payments.length && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-testid="payments-empty">Belum ada transaksi</TableCell></TableRow>}
              {payments.map((p) => (
                <TableRow key={p.order_id} data-testid={`payment-row-${p.order_id}`}>
                  <TableCell className="font-mono text-xs">{p.order_id.slice(0, 8)}...</TableCell>
                  <TableCell className="font-mono">{rupiah(p.gross_amount)}</TableCell>
                  <TableCell><span className={`px-2.5 py-0.5 rounded-full text-xs font-mono border ${STATUS_STYLE[p.status] || STATUS_STYLE.pending}`}>{p.status}</span></TableCell>
                  <TableCell className="text-xs font-mono uppercase">{p.payment_type || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{fmtDateTime(p.updated_at || p.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
